import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { Card, CardContent, CardHeader, CardTitle } from "@shadcn-in-nx/ui/card";
import { Button } from "@shadcn-in-nx/ui/button";
import { Loader2, BarChart3, Users, CheckCircle, Clock, RotateCw } from "lucide-react";
import BreakdownChart from "./BreakdownChart";
import UserDrillDown from "./UserDrillDown";

export default function BlueprintAnalytics() {
    const [data, setData] = useState(null);
    const [drillDown, setDrillDown] = useState(null); // { type: "role"|"industry", value: "Sales" }

    const { call: fetchAnalytics, isLoading } = useBluelagoon({
        method: "getBlueprintAnalytics",
    });

    const load = useCallback(async () => {
        const res = await fetchAnalytics({});
        if (res?.data) setData(res.data);
    }, [fetchAnalytics]);

    useEffect(() => { load(); }, [load]);

    if (isLoading && !data) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (!data) return null;

    const { totals } = data;

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">User Insights</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Analyze signup data by role, industry, and building goals
                    </p>
                </div>
                <Button variant="outline" onClick={load} disabled={isLoading}>
                    <RotateCw className="h-4 w-4 mr-2" />
                    Refresh
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total Users" value={totals.users} />
                <StatCard icon={CheckCircle} label="Categorized" value={totals.categorized} color="text-green-500" />
                <StatCard icon={Clock} label="Uncategorized" value={totals.uncategorized} color="text-amber-500" />
                <StatCard icon={BarChart3} label="Pending Requests" value={totals.pendingRequests} color="text-blue-500" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <BreakdownChart
                    title="By Role"
                    items={data.byRole}
                    total={totals.users}
                    onSelect={(name) => setDrillDown({ type: "role", value: name })}
                    activeValue={drillDown?.type === "role" ? drillDown.value : null}
                />
                <BreakdownChart
                    title="By Industry"
                    items={data.byIndustry}
                    total={totals.users}
                    onSelect={(name) => setDrillDown({ type: "industry", value: name })}
                    activeValue={drillDown?.type === "industry" ? drillDown.value : null}
                />
            </div>

            {data.byRoleIndustry?.length > 0 && (
                <BreakdownChart
                    title="Top Role + Industry Combos"
                    items={data.byRoleIndustry.map((r) => ({
                        name: `${r.role} / ${r.industry}`,
                        count: r.count,
                    }))}
                    total={totals.users}
                    onSelect={() => {}}
                />
            )}

            {drillDown && (
                <UserDrillDown
                    users={data.users}
                    filterType={drillDown.type}
                    filterValue={drillDown.value}
                    onClose={() => setDrillDown(null)}
                />
            )}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, color = "text-foreground" }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className={`text-2xl font-bold flex items-center gap-2 ${color}`}>
                    <Icon className="h-5 w-5" />
                    {value}
                </div>
            </CardContent>
        </Card>
    );
}
